import { useQuery } from '@apollo/client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { GET_ACTIVITY_FEED, type ActivityFeedItem } from './ActivityFeed.queries';
import useNotificationActions from './useNotificationActions';

const ACTIVITY_POLL_INTERVAL_MS = 10000;
const ACTIVITY_POLL_LIMIT = 15;
const ACTIVITY_NOTIFICATION_ID_PARAM = 'activityNotificationId';

export const ACTIVITY_ITEMS_UPDATED_EVENT =
  'ecosconnect:activity-items-updated';

const getActivityItemTime = (item: ActivityFeedItem) => Number(item.time) || 0;

const notificationToastStyles: Record<string, React.CSSProperties> = {
  button: {
    position: 'relative',
    width: 356,
    maxWidth: 'calc(100vw - 32px)',
    border: '1px solid rgba(18, 29, 43, 0.12)',
    borderRadius: 8,
    background: '#ffffff',
    boxShadow: '0 16px 40px rgba(18, 29, 43, 0.18)',
    color: '#1f2933',
    cursor: 'pointer',
    padding: '14px 44px 14px 16px',
    textAlign: 'left',
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: '28px',
    padding: 0,
    textAlign: 'center',
  },
  title: {
    color: '#102a43',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '18px',
    marginBottom: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    color: '#52606d',
    display: '-webkit-box',
    fontSize: 13,
    lineHeight: '18px',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
  },
};

export default function ActivityHandler(): null {
  const knownItemIdsRef = useRef<Set<string> | null>(null);
  const latestKnownTimeRef = useRef(0);
  const processedNotificationIdsRef = useRef(new Set<string>());
  const [activityFeedAfter, setActivityFeedAfter] = useState<
    number | undefined
  >(undefined);
  const [pendingMarkReadIds, setPendingMarkReadIds] = useState<string[]>([]);
  const history = useHistory();
  const location = useLocation();
  const { markRead } = useNotificationActions();
  const { data, loading } = useQuery(GET_ACTIVITY_FEED, {
    variables: { limit: ACTIVITY_POLL_LIMIT, after: activityFeedAfter },
    pollInterval: ACTIVITY_POLL_INTERVAL_MS,
  });

  const showActivityToast = useCallback(
    (notification: ActivityFeedItem, actionPath?: string) => {
      toast.custom(
        (toastId) => {
          const openNotification = async () => {
            toast.dismiss(toastId);

            if (typeof actionPath !== 'string') {
              return;
            }

            try {
              if (!notification.read) {
                await markRead(notification.id);
              }
            } finally {
              history.push(actionPath);
            }
          };

          return (
            <div
              role="button"
              tabIndex={0}
              style={notificationToastStyles.button}
              onClick={() => {
                void openNotification();
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') {
                  return;
                }
                event.preventDefault();
                void openNotification();
              }}
            >
              <button
                type="button"
                aria-label="Dismiss notification"
                title="Dismiss"
                style={notificationToastStyles.closeButton}
                onClick={(event) => {
                  event.stopPropagation();
                  toast.dismiss(toastId);
                }}
              >
                x
              </button>
              <div style={notificationToastStyles.title}>
                {notification.title}
              </div>
              <div style={notificationToastStyles.body}>
                {notification.body}
              </div>
            </div>
          );
        },
        {
          id: `activity-${notification.id}`,
          duration: 10000,
        }
      );
    },
    [history, markRead]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const activityNotificationId = params.get(ACTIVITY_NOTIFICATION_ID_PARAM);

    if (!activityNotificationId) {
      return;
    }

    params.delete(ACTIVITY_NOTIFICATION_ID_PARAM);

    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
      hash: location.hash,
    });

    if (processedNotificationIdsRef.current.has(activityNotificationId)) {
      return;
    }

    processedNotificationIdsRef.current.add(activityNotificationId);
    setPendingMarkReadIds((ids) => [...ids, activityNotificationId]);
  }, [history, location.hash, location.pathname, location.search]);

  // Marking read while the initial GetActivityFeed request is still in flight
  // loses the update: the refetch triggered by markRead deduplicates onto that
  // in-flight request, whose server snapshot predates the mutation, so stale
  // read=false overwrites the mutation result. Wait for the initial load to
  // settle before marking notifications from the URL as read.
  useEffect(() => {
    if (loading || pendingMarkReadIds.length === 0) {
      return;
    }

    setPendingMarkReadIds([]);
    pendingMarkReadIds.forEach((notificationId) => {
      markRead(notificationId).catch(() => {});
    });
  }, [loading, markRead, pendingMarkReadIds]);

  useEffect(() => {
    if (!data?.activityFeed) {
      return;
    }

    const items = data.activityFeed.items;
    const currentItemIds = new Set(items.map((item) => String(item.id)));
    const latestItemTime = Math.max(0, ...items.map(getActivityItemTime));

    if (knownItemIdsRef.current === null) {
      knownItemIdsRef.current = currentItemIds;
      latestKnownTimeRef.current = latestItemTime;
      setActivityFeedAfter(latestItemTime || undefined);
      return;
    }

    const newItems = items.filter(
      (item) =>
        !knownItemIdsRef.current?.has(String(item.id)) &&
        getActivityItemTime(item) > latestKnownTimeRef.current
    );

    currentItemIds.forEach((itemId) => knownItemIdsRef.current?.add(itemId));
    latestKnownTimeRef.current = Math.max(
      latestKnownTimeRef.current,
      latestItemTime
    );
    setActivityFeedAfter(latestKnownTimeRef.current || undefined);

    newItems.forEach((item) => {
      showActivityToast(
        item,
        typeof item.actionPath === 'string' ? item.actionPath : undefined
      );
    });

    if (newItems.length > 0) {
      window.dispatchEvent(
        new CustomEvent(ACTIVITY_ITEMS_UPDATED_EVENT, { detail: newItems })
      );
    }
  }, [data, showActivityToast]);

  return null;
}
